export interface JobPosting {
  id: string;
  organizationId: string;
  organizationName: string;
  jobId: string;
  title: string;
  location: string;
  workplaceType: 'Remote' | 'On-site' | 'Hybrid';
  employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  description: string;
  seniorityLevel: 'Entry' | 'Mid' | 'Senior' | 'Lead' | 'Director' | 'Executive';
  qualifications?: string;
  salaryRange?: string;
  /** Detail deadline is a date-only string ("YYYY-MM-DD") or null. */
  applicationDeadline?: string | null;
  /** Always "open" on the detail (expired postings 404 server-side). */
  status?: 'open' | 'closed';
  perks?: string;
  createdAt: string;
  updatedAt?: string;
  /**
   * Present only when the detail is fetched with a candidate bearer token
   * (see jobsApi.getById). Lets the UI block re-applying to a posting the
   * signed-in candidate has already applied to.
   */
  candidateApplicationContext?: {
    hasApplied: boolean;
    canReuseStoredResume?: boolean;
  };
}

// Lean shape returned by GET /public/jobs (list). Detail-only fields
// (organizationName/organization, qualifications, applicationDeadline, perks)
// are fetched per-job via GET /public/jobs/:id — see `JobPosting` above.
export interface JobPostingListItem {
  id: string;
  organizationId: string;
  jobId: string;
  title: string;
  location: string;
  workplaceType: 'Remote' | 'On-site' | 'Hybrid';
  employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  description: string;
  seniorityLevel: 'Entry' | 'Mid' | 'Senior' | 'Lead' | 'Director' | 'Executive';
  salaryRange?: string;
  /** Date-only ("YYYY-MM-DD") or null. Expired postings are omitted server-side. */
  applicationDeadline?: string | null;
  /** Always "open" for the public list. */
  status?: 'open' | 'closed';
  createdAt: string;
  /**
   * Present only when the list is fetched with a candidate token (see
   * jobsApi.getAll). True when the signed-in candidate already applied —
   * drives the "Applied" pill, consistent with the detail's hasApplied.
   */
  hasApplied?: boolean;
}

/** Pagination envelope returned by the paginated candidate endpoints. */
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** Item from GET /candidates/me/applications. */
export interface CandidateApplicationItem {
  id: string;
  recordKind: 'pending_application' | 'hiring_candidate';
  status: string;
  appliedAt: string | null;
  jobPostingId: string;
  jobPublicId: string;
  jobTitle: string;
  organizationId: string;
  organizationName: string;
  headline: string | null;
  rating: number | null;
}

/** Item from GET /candidates/me/saved-jobs. */
export interface SavedJobItem {
  savedId: string;
  jobPostingId: string;
  jobId: string;
  title: string;
  organizationId: string;
  organizationName: string | null;
  location: string | null;
  workplaceType: string | null;
  employmentType: string | null;
  seniorityLevel: string | null;
  salaryRange: string | null;
  /** Date-only ("YYYY-MM-DD") or null. */
  applicationDeadline: string | null;
  /** "closed" once unpublished, archived, or past deadline. */
  status: 'open' | 'closed';
  /** ISO timestamp. */
  savedAt: string;
}

/** Result of POST /candidates/me/saved-jobs. */
export interface SaveJobResult {
  savedId: string;
  jobPostingId: string;
  savedAt: string;
  alreadySaved: boolean;
}

export interface PaginatedData<T> {
  items: T[];
  pagination: Pagination;
}

/**
 * Activation checklist item kinds, as the API's checklist templates define them
 * (`ChecklistItemTypes` in ems-api). Kept open-ended: an unknown kind is shown
 * as a text answer rather than dropped.
 */
export type ChecklistItemType =
  | 'checkbox'
  | 'free_text'
  | 'file_upload'
  | 'date'
  | 'number'
  | 'dropdown'
  | 'multiple_choice';

/** One option of a checkbox / multiple-choice item; `val` is the candidate's tick. */
export interface ChecklistOption {
  key: string;
  label: string;
  val: boolean;
  [extra: string]: unknown;
}

/**
 * One entry of a choice item's template option list: a plain label for
 * `dropdown`, a `{ key, label, val }` object for `multiple_choice` / `checkbox`.
 */
export type ChecklistTemplateOptionValue = string | Record<string, unknown>;

/** Template side of a checklist row, with HR's per-candidate overrides already applied. */
export interface CandidateChecklistTemplateItemSummary {
  id: string;
  title: string;
  itemType: ChecklistItemType | string;
  description: string | null;
  isRequired: boolean;
  itemNumber: number;
  /**
   * The template's full option list for choice items (`dropdown`,
   * `multiple_choice`, `checkbox`), `null` for every other item type
   * (T102126041). The row's own `value` is only the candidate's saved answer.
   * Absent from API builds before ems-api #3657; the page then falls back to
   * the options the row's `value` was seeded with.
   */
  values?: ChecklistTemplateOptionValue[] | null;
}

/** One activation-stage checklist row (GET /candidate/checklist-portal/:candidateId). */
export interface CandidateChecklistItem {
  id: string;
  /** HR review state: pending | approved | rejected. */
  status: string;
  remarks: string | null;
  /** Storage key of an uploaded file (file_upload items). */
  file: string | null;
  /** Shape depends on `templateItem.itemType`. */
  value: unknown;
  /**
   * ISO time the candidate last saved a `value`; `null` while the row still
   * holds its template seed (T102126063). Set on every value save, even one
   * equal to the seed (a one-option dropdown), so it tells an answered dropdown
   * from an untouched one. Absent from API builds before ems-api #3667.
   */
  respondedAt?: string | null;
  templateItem: CandidateChecklistTemplateItemSummary | null;
}

export interface CandidateChecklistActivation {
  id: string;
  /** pending | approved | rejected …; responses are accepted only while pending or approved. */
  status: string;
  /** Percentage of items HR has approved. */
  progress: number;
  filesUploaded: string | null;
  appliedPosition: string;
  applicationDate: string;
}

/** Data of GET /candidate/checklist-portal/:candidateId. */
export interface CandidateChecklistPortal {
  candidate: {
    id: string;
    firstName: string;
    lastName: string;
    status: string;
    appliedAt: string | null;
  };
  job: {
    id: string;
    jobId: string;
    title: string;
    location: string | null;
    workplaceType: string | null;
    employmentType: string | null;
    description: string | null;
  };
  organization: { id: string; name: string };
  activation: CandidateChecklistActivation | null;
  checklistItems: CandidateChecklistItem[];
}

/**
 * Row returned by the checklist PATCH and upload endpoints. It is the raw
 * checklist row, so its `templateItem` carries the template's own title rather
 * than HR's override: callers merge only the answer fields from it.
 */
export interface CandidateChecklistItemUpdate {
  id: string;
  status: string;
  remarks: string | null;
  file: string | null;
  value: unknown;
  /** Stamped by the save that returned this row (T102126063); absent before ems-api #3667. */
  respondedAt?: string | null;
}

/** Data of GET …/checklist-items/:itemId/view-url. */
export interface CandidateChecklistFileUrl {
  url: string;
  expiresIn: number;
  expiresAt: string;
}

export interface JobPostingsResponse {
  success: boolean;
  statusCode: number;
  data: JobPostingListItem[];
  message: string;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * A subscription plan as exposed by the public marketing pricing endpoint
 * (GET /public/pricing). Lean projection — mirrors the backend PublicPlanDto.
 * Prices are in major currency units (e.g. dollars); the backend serves only
 * this allowlist (no Stripe IDs or internal catalog fields).
 */
export interface PublicPlan {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceYearly: number;
  /** Base catalog currency of priceMonthly/priceYearly (lowercase ISO). Optional: older backends omit it (= cad). */
  currency?: string;
  /**
   * Per-currency price points keyed by lowercase ISO code (base currency always
   * present; a non-base currency only when the plan is really sold in it —
   * configured prices, never FX conversions). Optional so the page degrades to
   * base-currency display against a backend that predates the field.
   * seatMonthly/seatYearly (core repackage): the monthly/yearly are the flat
   * base fee (covers the owner); the seat amounts are billed per purchased
   * employee seat, present only when the plan has per-seat pricing.
   */
  prices?: Record<
    string,
    { monthly: number; yearly: number; seatMonthly?: number; seatYearly?: number }
  >;
  planType: 'base' | 'addon';
  /**
   * True only for the free-trial edition. The page cannot infer this from a
   * price of 0: an unpriced "contact us" tier is 0 too, and must keep reading
   * "Custom" rather than "Free". Optional so the page degrades against a
   * backend that predates the field — an absent flag simply means "not a
   * trial", which is the pre-change behaviour.
   */
  isTrial?: boolean;
  maxUsers: number;
  maxStorageGb: number;
  includedTokensMonth: number;
  features: string[];
}

export interface PublicPlansResponse {
  success: boolean;
  statusCode: number;
  data: PublicPlan[];
  message: string;
}

/**
 * Viewer pricing context (GET /public/pricing/geo): the display currency the
 * backend resolved from the request's network location (CloudFront viewer
 * country — Canada sees CAD, everyone else USD, constrained to a currency the
 * catalog can sell). Per-viewer and uncached, unlike /public/pricing.
 */
export interface PublicGeoContext {
  /** ISO-3166 alpha-2 viewer country; null when no edge geo header is present. */
  country: string | null;
  /** Lowercase ISO display currency the viewer should see. */
  currency: string;
}

export interface PublicGeoContextResponse {
  success: boolean;
  statusCode: number;
  data: PublicGeoContext;
  message: string;
}

export interface JobApplication {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  headline?: string;
  professionalSummary?: string;
  resumeUrl?: string;
  totalExperienceYears?: number;
  workExperience?: Array<{
    company: string;
    position: string;
    startDate?: string;
    endDate?: string;
    description?: string;
  }>;
  educationEntries?: Array<{
    institution: string;
    degree: string;
    fieldOfStudy: string;
    startDate?: string;
    endDate?: string;
  }>;
  certificationEntries?: Array<{
    name: string;
    issuer: string;
    issueDate: string;
    expiryDate: string;
    credentialId?: string;
    awardedTo?: string;
  }>;
  skills?: string[];
  expectedSalary?: string;
  country?: string;
}

export interface JobApplicationResponse {
  success: boolean;
  statusCode: number;
  data: {
    id: string;
    jobPostingId: string;
    candidateId: string;
    status: string;
    appliedAt: string;
    createdAt: string;
    updatedAt: string;
  };
  message: string;
}

export interface CandidateRegistrationRequest {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export interface CandidateAuthenticatedData {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export interface CandidatePendingVerificationData {
  email: string;
  requiresEmailVerification: true;
}

export type CandidateRegistrationData =
  | CandidateAuthenticatedData
  | CandidatePendingVerificationData;

export interface CandidateRegistrationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: CandidateRegistrationData;
}

export interface CandidateLoginResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: CandidateAuthenticatedData;
}

/**
 * POST /auth/refresh — the rotated tokens arrive FLAT on `data` (NOT nested
 * under `tokens`, unlike login). `refreshToken` is present for body-token
 * clients (the JMS/candidate realm sends its refresh token in the body and
 * receives the rotated one the same way).
 */
export interface CandidateRefreshData {
  accessToken: string;
  refreshToken?: string;
  realm?: string;
}

export interface CandidateRefreshResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: CandidateRefreshData;
}

export const isPendingVerification = (
  data: CandidateRegistrationData
): data is CandidatePendingVerificationData =>
  (data as CandidatePendingVerificationData).requiresEmailVerification === true;

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

export interface AuthErrorBody {
  success?: boolean;
  statusCode?: number;
  error?: string;
  message?: string;
  code?: string;
  /** ISO-8601 timestamp until which the account is locked (403 ACCOUNT_LOCKED). */
  lockedUntil?: string;
  /**
   * Server-computed seconds remaining on the lockout (403 ACCOUNT_LOCKED).
   * Preferred over `lockedUntil` for countdowns — it is relative, so a skewed
   * client clock cannot distort the remaining time.
   */
  retryAfterSeconds?: number;
}

export interface ResumeReaderResponse {
  success: boolean;
  statusCode: number;
  data: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    headline?: string | null;
    professionalSummary?: string | null;
    totalExperienceYears?: number | null;
    workExperience?: Array<{
      company: string;
      position?: string;
      jobTitle?: string;
      startDate?: string;
      endDate?: string;
      description?: string;
    }> | null;
    educationEntries?: Array<{
      institution: string;
      degree: string;
      fieldOfStudy: string;
      startDate?: string;
      endDate?: string;
      startYear?: string | number | null;
      endYear?: string | number | null;
    }> | null;
    certificationEntries?: Array<{
      name: string;
      issuer: string;
      issueDate: string;
      expiryDate: string;
      credentialId?: string;
      awardedTo?: string;
    }> | null;
    skills?: string[] | null;
    expectedSalary?: string | null;
    country?: string | null;
    others?: string[] | null;
  };
  message: string;
}

